"use client";

// ContextMenu — the one frosted, rounded (6px) command surface used across the OS:
// right-click a folder/file (rename · new · move), and reused by the download dock
// and the task list. Rendered in a PORTAL to <body> so its fixed position is always
// viewport-relative (the OS surface is transformed, which would otherwise capture a
// `position: fixed` child). Opens AT the cursor and flips left/up when there isn't
// room — natural right-click behaviour. Closes on outside-click / Escape / scroll.

import { createPortal } from "react-dom";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** render a thin divider above this item */
  separated?: boolean;
  /** muted right-aligned hint (e.g. a shortcut, or "soon") */
  hint?: string;
  /** the row that is currently chosen, when the menu is a lens rather than a
   *  list of actions — marked with a quiet ink-dot in the icon slot (unless the
   *  row brought an icon of its own) and `data-on` for styling. */
  selected?: boolean;
}

export interface MenuState {
  x: number;
  y: number;
  items: MenuItem[];
  /** when the menu is anchored to a trigger element (not a bare cursor point),
   *  its bounding-rect top/bottom — lets the flip logic pick whichever side of
   *  the trigger actually has room, instead of assuming a top-anchored point. */
  anchorTop?: number;
  anchorBottom?: number;
}

export function useContextMenu() {
  const [menu, setMenu] = useState<MenuState | null>(null);
  const open = useCallback((e: { clientX: number; clientY: number; preventDefault?: () => void }, items: MenuItem[]) => {
    e.preventDefault?.();
    setMenu({ x: e.clientX, y: e.clientY, items });
  }, []);
  /** open ANCHORED TO A BUTTON rather than at a bare point. The menu then opens
   *  from an edge of the trigger — below it when there is room, above it when
   *  there isn't — so it always appears against the control you pressed and the
   *  pointer never has to travel to reach it. A bare point can only guess. */
  const openAt = useCallback((el: HTMLElement, items: MenuItem[]) => {
    const r = el.getBoundingClientRect();
    setMenu({ x: r.left, y: r.top, items, anchorTop: r.top, anchorBottom: r.bottom });
  }, []);
  const close = useCallback(() => setMenu(null), []);
  return { menu, open, openAt, close };
}

export default function ContextMenu({
  menu,
  onClose,
  footer,
}: {
  menu: MenuState | null;
  onClose: () => void;
  /** optional row rendered after the items (e.g. the undo/redo pair) — its
   *  buttons do NOT auto-close the menu, so actions can chain. */
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // measure, then anchor — flip to the left of / above the cursor when the menu
  // would overflow, and clamp to the viewport as a final safety. When the menu is
  // anchored to a trigger element (anchorTop/anchorBottom present, e.g. a button
  // in a bottom action bar) the vertical decision is made against BOTH edges of
  // that trigger's rect — whichever side actually has room — instead of a single
  // point, so a bottom-anchored button correctly opens upward ending at its own
  // top edge rather than overlapping it.
  //
  // Depends on `mounted` too, not just `menu`: this component unmounts entirely
  // when closed (the caller conditionally renders it), so `mounted` starts false
  // on every fresh open and the component still returns null on that first
  // render — meaning `ref.current` is null and this would otherwise measure off
  // the 200×220 fallback forever. Re-running once `mounted` flips true forces a
  // real remeasure against the actual rendered menu, which is what the anchored
  // (flush-to-button-edge) case needs to land in the right place.
  useLayoutEffect(() => {
    if (!menu) { setPos(null); return; }
    const el = ref.current;
    const w = el?.offsetWidth ?? 200;
    const h = el?.offsetHeight ?? 220;
    const m = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = menu.x;
    if (menu.x + w + m > vw) left = menu.x - w; // not enough room right → open to the left
    left = Math.max(m, Math.min(left, vw - w - m));

    let top: number;
    if (menu.anchorTop != null && menu.anchorBottom != null) {
      const roomBelow = vh - menu.anchorBottom - m;
      const roomAbove = menu.anchorTop - m;
      if (h <= roomBelow) {
        top = menu.anchorBottom; // fits below the trigger → open downward from its bottom edge
      } else if (h <= roomAbove) {
        top = menu.anchorTop - h; // fits above → open upward, ending at the trigger's top edge
      } else {
        // fits in neither direction — pick the side with more room and clamp.
        top = roomBelow >= roomAbove ? menu.anchorBottom : menu.anchorTop - h;
      }
    } else {
      top = menu.y;
      if (menu.y + h + m > vh) top = menu.y - h; // not enough room below → open above
    }
    top = Math.max(m, Math.min(top, vh - h - m));
    setPos({ left, top });
  }, [menu, mounted]);

  useEffect(() => {
    if (!menu) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    const onScroll = () => onClose();
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("blur", onClose);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("blur", onClose);
      window.removeEventListener("resize", onClose);
    };
  }, [menu, onClose]);

  if (!menu || !mounted) return null;
  const node = (
    <div
      ref={ref}
      className="ctx-menu"
      role="menu"
      data-gesture-root=""
      // hidden for the first frame so the measure→flip happens before paint (no jump)
      style={{ left: pos?.left ?? menu.x, top: pos?.top ?? menu.y, visibility: pos ? "visible" : "hidden" }}
    >
      {menu.items.map((it, i) => (
        <button
          key={i}
          type="button"
          role="menuitem"
          className={`ctx-item${it.danger ? " ctx-item--danger" : ""}${it.separated ? " ctx-item--sep" : ""}`}
          disabled={it.disabled}
          data-on={it.selected || undefined}
          aria-current={it.selected ? "true" : undefined}
          onClick={() => { it.onSelect?.(); onClose(); }}
        >
          <span className="ctx-ico" aria-hidden>{it.icon ?? (it.selected ? <span className="ink-dot" /> : null)}</span>
          <span className="ctx-label">{it.label}</span>
          {it.hint ? <span className="ctx-hint">{it.hint}</span> : null}
        </button>
      ))}
      {footer ?? null}
    </div>
  );
  return createPortal(node, document.body);
}
